/**
 * Checks before a version goes to review: what would stop the request, and
 * what would look different on the live page than in the editor.
 */
import { checkWidgetCode } from 'shared/lib/widgetCode/normalizeWidgetCode';
import type { DiffComponent, PageDiff } from '~/lib/pageDiff';

export type CheckLevel = 'block' | 'warn' | 'ok';
export type PublishCheck = { id: string; level: CheckLevel; text: string; items?: string[] };

type Page = { state?: string | null; commitedVersion?: string | null; metadata?: Array<{ key: string; value: string }> | null };
type Slot = { slotId: string; components: Array<DiffComponent | null> };
/** From customWidgetLiveVersions: the version live pages render, null when they render nothing. */
export type WidgetLive = { isActive: boolean; liveVersion: string | null };

// Problems in pasted code that change how the live page works or looks.
const LIVE_AI_FINDINGS = ['no-html', 'inline-handlers', 'templated-handlers', 'outside-images', 'relative-images', 'base64-images', 'external-scripts', 'document-write', 'size', 'inline-animation'];

const each = (slots: Slot[], visit: (c: DiffComponent) => void) => {
  const walk = (c: DiffComponent | null) => {
    if (!c) return;
    visit(c);
    (c.subcomponents || []).forEach(walk);
  };
  (slots || []).forEach(s => (s.components || []).forEach(walk));
};

const unique = (xs: string[]) => Array.from(new Set(xs));

/** The custom widgets the sections use by name (instances that get theirs from a preset are left out). */
export const customWidgetNames = (slots: Slot[]): string[] => {
  const names: string[] = [];
  each(slots, (c) => {
    const name = c.widgetId === 'CustomWidget' && c.props && c.props.customWidgetName;
    if (typeof name === 'string' && name) names.push(name);
  });
  return unique(names).sort();
};

export const publishChecks = ({ page, slots, diff, nameOf, widgets, compared = true, firstVersion = false }: {
  page: Page;
  slots: Slot[];
  diff: PageDiff;
  nameOf: (c: DiffComponent) => string;
  /** Live versions by widget name, once loaded. */
  widgets?: Record<string, WidgetLive>;
  /** False when the published version couldn't be loaded, so there is no comparison. */
  compared?: boolean;
  /** The page isn't live (new, or withdrawn): nothing to compare with. */
  firstVersion?: boolean;
}): PublishCheck[] => {
  const checks: PublishCheck[] = [];

  if (page.state === 'UNDER_REVIEW') {
    const which = page.commitedVersion ? `Version ${page.commitedVersion}` : 'Another version';
    checks.push({ id: 'under-review', level: 'block', text: `${which} is waiting for review. A reviewer has to approve or decline it before you can ask again.` });
  } else if (page.state === 'SCHEDULED') {
    checks.push({ id: 'scheduled', level: 'block', text: 'This page is scheduled to deploy. Ask for another review after that deployment.' });
  } else if (page.state === 'PENDING_DEPLOY') {
    // Not a block: replacing an approved version that hasn't gone out yet can be what's wanted.
    const which = page.commitedVersion ? `Version ${page.commitedVersion}` : 'Another version';
    checks.push({ id: 'pending-deploy', level: 'warn', text: `${which} is approved and waiting to deploy. Asking for review of this one takes its place.` });
  } else if (page.state === 'DELETED') {
    checks.push({ id: 'deleted', level: 'block', text: 'This page is deleted.' });
  } else if (page.state === 'PENDING_DELETE' || page.state === 'PENDING_REMOVAL') {
    checks.push({
      id: 'pending-request',
      level: 'block',
      text: `${page.state === 'PENDING_DELETE' ? 'Deletion' : 'Withdrawal'} of this page is waiting for an approver. They have to decide on it before you can ask for review.`
    });
  }

  if (firstVersion) {
    if (!(slots || []).some(s => (s.components || []).some(Boolean))) checks.push({ id: 'empty-page', level: 'warn', text: 'This page has no sections yet.' });
  } else if (compared && !diff.sections.length && !diff.settings.length) {
    checks.push({ id: 'no-changes', level: 'warn', text: 'Nothing changed since the published version.' });
  }

  const pinned: string[] = [];
  const pinnedWidgets: string[] = [];
  const empty: string[] = [];
  const ai: string[] = [];
  each(slots, (c) => {
    const widget = c.widgetId === 'CustomWidget' && c.props && typeof c.props.customWidgetName === 'string' ? c.props.customWidgetName : '';
    const version = widget ? c.props.customWidgetVersion : undefined;
    const live = widget && widgets ? widgets[widget] : undefined;
    // Draft pins and unpinned sections show the widget's live version; with none, live pages show nothing there.
    if (live && (!live.isActive || ((!version || version === 'draft') && !live.liveVersion))) {
      empty.push(`${nameOf(c)}: ${live.isActive ? `“${widget}” has no published version` : `“${widget}” is archived or doesn’t exist`}`);
    } else if (widget && version === 'draft') {
      pinned.push(nameOf(c));
      pinnedWidgets.push(widget);
    }
    if (c.widgetId === 'AiGeneratedWidget' && c.props && c.props.content) {
      const content = c.props.content;
      const { findings } = checkWidgetCode({ html: content.html || '', css: content.css || '', js: content.js || '' }, content.scopeClass || 'ai-widget-check');
      findings.filter(f => LIVE_AI_FINDINGS.includes(f.id)).forEach(f => ai.push(`${nameOf(c)}: ${f.title}`));
    }
  });
  if (empty.length) {
    checks.push({
      id: 'empty-widgets',
      level: 'warn',
      text: `${empty.length === 1 ? 'A section is' : `${empty.length} sections are`} empty on the live page: the custom widget has nothing published. The preview shows a note in its place.`,
      items: unique(empty)
    });
  }
  if (pinned.length) {
    const names = unique(pinned);
    const versions = widgets ? unique(pinnedWidgets).map(w => widgets[w] && widgets[w].liveVersion).filter(Boolean) : [];
    checks.push({
      id: 'draft-pins',
      level: 'warn',
      text: `${names.length === 1 ? 'A custom widget is set to its unpublished draft' : `${names.length} custom widgets are set to their unpublished drafts`}. Live pages show ${versions.length === 1 ? `its published version (v${versions[0]})` : 'their published versions'} instead.`,
      items: names
    });
  }
  if (ai.length) {
    checks.push({ id: 'ai-sections', level: 'warn', text: 'Pasted sections have problems that show on the live page. Open them for the fixes.', items: unique(ai) });
  }

  const meta = new Map((page.metadata || []).map(m => [m.key, (m.value || '').trim()]));
  const missing = ['title', 'description'].filter(k => !meta.get(k));
  if (missing.length) {
    checks.push({ id: 'seo', level: 'warn', text: `No SEO ${missing.join(' or ')} in Page settings.` });
  }

  if (!checks.some(c => c.level !== 'ok')) checks.push({ id: 'all-clear', level: 'ok', text: 'No problems found.' });
  return checks;
};
